import { confirmDialog } from '../ui/ConfirmDialog';
import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { supabase } from '../../lib/supabase';

// Two-step verification (TOTP) for super admins — audit SEC-P3-03.
// Once a super admin has a verified factor, is_super_admin() only returns true
// for an aal2 session (migration 20260930031500), so the CRM asks for the code
// right after login and whenever a session is still aal1.

/** True while the signed-in user has a verified factor but this session is not yet aal2. */
export function useMfaRequired(userId: string | undefined): [boolean, () => void] {
  const [required, setRequired] = useState(false);
  const check = useCallback(async () => {
    if (!userId || !supabase) { setRequired(false); return; }
    const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    setRequired(data?.nextLevel === 'aal2' && data.currentLevel !== 'aal2');
  }, [userId]);
  useEffect(() => { void check(); }, [check]);
  return [required, check];
}

async function verifyTotp(factorId: string, code: string): Promise<string | null> {
  const { error } = await supabase!.auth.mfa.challengeAndVerify({ factorId, code: code.trim() });
  return error ? 'Código incorrecto o vencido. Intenta de nuevo.' : null;
}

const inputCls = 'w-full rounded-xl border border-slate-400/40 bg-transparent px-4 py-3 text-center text-lg tracking-[0.4em] outline-none focus:border-indigo-500';
const buttonCls = 'rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50';

/** Full-screen prompt shown instead of the CRM until the session is aal2. */
export function MfaChallenge({ onVerified }: { onVerified: () => void }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    const { data } = await supabase!.auth.mfa.listFactors();
    const factor = data?.totp.find(f => f.status === 'verified');
    const err = factor ? await verifyTotp(factor.id, code) : 'No hay un autenticador configurado.';
    setBusy(false);
    if (err) setError(err); else onVerified();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4 text-slate-900">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4 rounded-2xl bg-white p-6 shadow-lg">
        <div className="flex items-center gap-2 font-semibold"><ShieldCheck size={20} className="text-indigo-600" /> Verificación en dos pasos</div>
        <p className="text-sm text-slate-600">Ingresa el código de 6 dígitos de tu app de autenticación.</p>
        <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric" autoComplete="one-time-code" autoFocus className={inputCls} aria-label="Código de verificación" />
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button type="submit" disabled={busy || code.length !== 6} className={`${buttonCls} w-full`}>Verificar</button>
        <button type="button" onClick={() => supabase!.auth.signOut()} className="w-full text-sm text-slate-500 underline">Cerrar sesión</button>
      </form>
    </div>
  );
}

/** Settings → Seguridad: enroll or remove the authenticator app. */
export function MfaSettings() {
  const [verifiedId, setVerifiedId] = useState<string | null>(null);
  const [enrolling, setEnrolling] = useState<{ id: string; qr: string; secret: string } | null>(null);
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await supabase!.auth.mfa.listFactors();
    setVerifiedId(data?.totp.find(f => f.status === 'verified')?.id ?? null);
  }, []);
  useEffect(() => { void load(); }, [load]);

  const start = async () => {
    setBusy(true);
    setMsg('');
    // Drop abandoned, never-verified attempts first.
    const { data: existing } = await supabase!.auth.mfa.listFactors();
    for (const f of existing?.all ?? []) {
      if (f.status !== 'verified') await supabase!.auth.mfa.unenroll({ factorId: f.id });
    }
    const { data, error } = await supabase!.auth.mfa.enroll({ factorType: 'totp' });
    setBusy(false);
    if (error || !data) { setMsg('No se pudo iniciar la configuración. Intenta de nuevo.'); return; }
    setEnrolling({ id: data.id, qr: data.totp.qr_code, secret: data.totp.secret });
  };

  const confirm = async () => {
    if (!enrolling) return;
    setBusy(true);
    const err = await verifyTotp(enrolling.id, code);
    setBusy(false);
    if (err) { setMsg(err); return; }
    setEnrolling(null);
    setCode('');
    setMsg('Verificación en dos pasos activada. Se pedirá el código en cada inicio de sesión.');
    void load();
  };

  const remove = async () => {
    if (!verifiedId || !await confirmDialog('¿Desactivar la verificación en dos pasos?')) return;
    const { error } = await supabase!.auth.mfa.unenroll({ factorId: verifiedId });
    setMsg(error ? 'No se pudo desactivar. Vuelve a iniciar sesión e intenta de nuevo.' : 'Verificación en dos pasos desactivada.');
    void load();
  };

  return (
    <div className="max-w-xl space-y-3 rounded-2xl border border-slate-400/30 p-5">
      <div className="flex items-center gap-2 font-semibold"><ShieldCheck size={18} className="text-indigo-500" /> Verificación en dos pasos</div>
      {verifiedId ? (
        <>
          <p className="text-sm opacity-80">Activa. Se pide un código de tu app de autenticación al iniciar sesión.</p>
          <button type="button" onClick={remove} className="text-sm text-red-500 underline">Desactivar</button>
        </>
      ) : enrolling ? (
        <div className="space-y-3">
          <p className="text-sm opacity-80">Escanea el código con Google Authenticator, 1Password u otra app y escribe el código que muestra.</p>
          <img src={enrolling.qr} alt="Código QR para la app de autenticación" className="h-44 w-44 rounded-lg bg-white p-2" />
          <p className="break-all text-xs opacity-70">Clave manual: {enrolling.secret}</p>
          <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric" autoComplete="one-time-code" className={inputCls} aria-label="Código de verificación" />
          <button type="button" onClick={confirm} disabled={busy || code.length !== 6} className={buttonCls}>Confirmar</button>
        </div>
      ) : (
        <>
          <p className="text-sm opacity-80">Protege tu cuenta de super administrador con un código de una app de autenticación.</p>
          <button type="button" onClick={start} disabled={busy} className={buttonCls}>Activar</button>
        </>
      )}
      {msg && <p className="text-sm opacity-90">{msg}</p>}
    </div>
  );
}
